import {
  RunningTimesOffPeriodsWithoutDetail,
  useRunningTimesOffPeriodsWithoutDetail
} from '@domain/running-times'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import moment from 'moment-timezone'
import React, {useEffect, useState} from 'react'

import {AmendStoppagesNavigator, NewStoppagesBanner, StoppagesListNavigator} from '../components'
import {useDowntimePlantId} from '../providers'

export type StoppagesNavigatorView = 'list' | 'navigator' | null

const StoppagesNavigator: React.FC<{
  view: StoppagesNavigatorView
  setView: (view: StoppagesNavigatorView) => void
}> = ({view, setView}) => {
  const plantId = useDowntimePlantId()
  const timezone = useUpmPlantTimezone(plantId)
  const startOfToday = moment.utc(new Date()).tz(timezone).startOf('day')
  const stoppagesWithoutDetailTimeRange = {
    from: startOfToday.clone().subtract(3, 'month').toDate(),
    to: startOfToday.clone().endOf('day').toDate()
  }

  const [stoppages, setStoppages] = useState<RunningTimesOffPeriodsWithoutDetail[]>([])
  const prefillMainEquipment = useCommonFeatureFlag(FeatureFlagKey.MainEquipmentPrefill, {
    plantId
  })

  const {
    data: newStoppages,
    isInitialLoading,
    isSuccess,
    isError,
    refetch,
    isRefetching
  } = useRunningTimesOffPeriodsWithoutDetail(plantId, {
    from: stoppagesWithoutDetailTimeRange.from.toJSON(),
    to: stoppagesWithoutDetailTimeRange.to.toJSON()
  })

  useEffect(() => {
    if (newStoppages && newStoppages?.length > 0) {
      /**
       * This api is prefetched in the DowntimeDashboard component, but react-query doesn't execute onSuccess callback for a cache hit.
       * hence, we need to set data in the state with useEffect
       */
      setStoppages(newStoppages)
    }
  }, [newStoppages])

  const removeStoppage = (stoppage: RunningTimesOffPeriodsWithoutDetail) => {
    setStoppages((prev) =>
      prev.filter(
        (_stoppage) =>
          !(
            _stoppage.equipment.id === stoppage.equipment.id &&
            _stoppage.runningTime.id === stoppage.runningTime.id
          )
      )
    )
  }

  return (
    <DataContentWrapper
      showLoader={isInitialLoading}
      isError={isError}
      data={newStoppages}
      retryFunction={() => void refetch()}
      isRefetching={isRefetching}
      progressSx={{color: 'primary.main'}}
    >
      {view === 'list' && (
        <StoppagesListNavigator
          stoppages={stoppages}
          onViewChange={setView}
          removeStoppage={removeStoppage}
          isLoading={!isSuccess}
        />
      )}
      {view === 'navigator' && (
        <AmendStoppagesNavigator
          stoppages={stoppages}
          onViewChange={setView}
          removeStoppage={removeStoppage}
          prefillMainEquipment={prefillMainEquipment}
        />
      )}
    </DataContentWrapper>
  )
}

type Props = {
  newStoppagesCount: number
}

export const NewStoppagesBannerContainer: React.FC<Props> = ({newStoppagesCount}) => {
  const [view, setView] = useState<StoppagesNavigatorView>(null)

  return (
    <CardBox
      data-test-id="new-stoppages-banner"
      sx={{
        height: 'calc(100vh - 166px)',
        maxHeight: '925px',
        overflow: 'hidden'
      }}
    >
      {view !== null ? (
        <StoppagesNavigator view={view} setView={setView} />
      ) : (
        <NewStoppagesBanner newStoppagesCount={newStoppagesCount} setView={setView} />
      )}
    </CardBox>
  )
}
